import { Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';
import type { EditorContext, QueryResult } from '../../types';

/**
 * Embedded work item query results — ADO syntax:
 *
 *   ::: query-table <query-guid>
 *   :::
 *
 * The node keeps the id; the view runs the query through the BFF (as the
 * user) and renders a live table so the author sees what readers will see.
 */

export interface AdoQueryOptions {
  ctx: EditorContext | null;
}

const GUID = '[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}';
const BLOCK = new RegExp(`^:::[ \\t]*query-table[ \\t]+(${GUID})[ \\t]*\\n:::[ \\t]*(?:\\n+|$)`);

function cell(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.length > 120 ? `${v.slice(0, 117)}…` : v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function QueryView({ node, extension, selected, updateAttributes }: NodeViewProps) {
  const ctx = (extension.options as AdoQueryOptions).ctx;
  const queryId = String(node.attrs.queryId ?? '');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!ctx || !queryId) return;
    setLoading(true);
    setError(null);
    ctx.api
      .runQuery(ctx.project, queryId)
      .then(setResult)
      .catch(e => setError(e?.message ?? 'Failed to run query'))
      .finally(() => setLoading(false));
  }, [ctx, queryId]);

  useEffect(load, [load]);

  const adoLink = ctx ? `https://dev.azure.com/${ctx.adoOrg}/${encodeURIComponent(ctx.project)}/_queries/query/${queryId}` : undefined;
  const wiLink = (id: number) => (ctx ? `https://dev.azure.com/${ctx.adoOrg}/${encodeURIComponent(ctx.project)}/_workitems/edit/${id}` : '#');

  return (
    <NodeViewWrapper className={`ado-query${selected ? ' is-selected' : ''}`} contentEditable={false}>
      <div className="ado-query-header">
        <span className="ado-macro-label">
          Query results <code>::: query-table</code>
        </span>
        <span className="ado-query-actions">
          {result && <span className="ado-query-count">{result.total} item{result.total === 1 ? '' : 's'}</span>}
          <button type="button" onClick={load} disabled={loading}>{loading ? 'Running…' : 'Refresh'}</button>
          {adoLink && <a href={adoLink} target="_blank" rel="noreferrer">Open query</a>}
          <button
            type="button"
            onClick={() => {
              const id = window.prompt('Query id (GUID)', queryId);
              if (id && new RegExp(`^${GUID}$`).test(id.trim())) updateAttributes({ queryId: id.trim() });
            }}
          >
            Change
          </button>
        </span>
      </div>
      {error && <div className="ado-query-error">{error}</div>}
      {!error && result && (
        <div className="ado-query-table-wrap">
          <table className="ado-query-table">
            <thead>
              <tr>
                {result.columns.map(c => (
                  <th key={c.referenceName}>{c.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map(r => (
                <tr key={r.id}>
                  {result.columns.map(c => (
                    <td key={c.referenceName}>
                      {c.referenceName === 'System.Id' ? (
                        <a href={wiLink(r.id)} target="_blank" rel="noreferrer">{r.id}</a>
                      ) : (
                        cell(r.fields[c.referenceName])
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {result.total > result.rows.length && <div className="ado-macro-hint">Showing first {result.rows.length} of {result.total}.</div>}
        </div>
      )}
      {!error && !result && !loading && <div className="ado-macro-hint">Query id: {queryId || '(none)'}</div>}
    </NodeViewWrapper>
  );
}

export const AdoQuery = Node.create<AdoQueryOptions>({
  name: 'adoQuery',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,

  addOptions() {
    return { ctx: null };
  },

  addAttributes() {
    return {
      queryId: {
        default: null,
        parseHTML: el => el.getAttribute('data-query-id'),
        renderHTML: attrs => ({ 'data-query-id': attrs.queryId }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-type="ado-query"]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', { 'data-type': 'ado-query', class: 'ado-query', ...HTMLAttributes }, `Query results: ${HTMLAttributes['data-query-id'] ?? ''}`];
  },

  renderText({ node }) {
    return `::: query-table ${node.attrs.queryId}\n:::`;
  },

  markdownTokenName: 'adoQuery',
  markdownTokenizer: {
    name: 'adoQuery',
    level: 'block',
    start(src) {
      return src.indexOf('::: query-table') >= 0 ? src.indexOf('::: query-table') : src.indexOf(':::query-table');
    },
    tokenize(src) {
      const m = BLOCK.exec(src);
      if (!m) return undefined;
      return { type: 'adoQuery', raw: m[0], queryId: m[1] };
    },
  },
  parseMarkdown(token, h) {
    return h.createNode('adoQuery', { queryId: token.queryId });
  },
  renderMarkdown(node) {
    return `::: query-table ${node.attrs?.queryId}\n:::`;
  },

  addNodeView() {
    return ReactNodeViewRenderer(QueryView);
  },
});
